import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, Loader2, Save, FileBox } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { API, TRANSLATIONS, authHeader, sendSermonToEditor } from "./shared";
import { SkeletonDeck, EmptyState, EXAMPLE_SCRIPTURES } from "./_shared_ui";
import ExportMenu from "./ExportMenu";

export default function ScriptureSlidesPanel({ t, onSaved }) {
  const navigate = useNavigate();
  const [scripture, setScripture] = useState("");
  const [translation, setTranslation] = useState("kjv");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [deck, setDeck] = useState(null);

  const generate = async () => {
    if (!scripture.trim()) {
      toast.error(t("provide_scripture") || "Provide a scripture reference");
      return;
    }
    setLoading(true);
    setDeck(null);
    try {
      const res = await axios.post(`${API}/api/pulpit/scripture-slides`, {
        scripture: scripture.trim(), translation, title: title || null,
        include_intro: true, include_application: true,
      }, { headers: authHeader() });
      setDeck(res.data.deck);
      toast.success(t("slides_generated") || "Slides generated");
    } catch (err) {
      toast.error(getErrorMessage(err, t("generation_failed") || "Generation failed"));
    } finally { setLoading(false); }
  };

  const saveDeck = async () => {
    if (!deck) return;
    try {
      await axios.post(`${API}/api/pulpit/sermons`, {
        title: deck.title || title || scripture,
        sermon_type: "slides", scripture, content: deck,
      }, { headers: authHeader() });
      toast.success(t("sermon_saved") || "Saved to library");
      onSaved && onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, t("save_failed") || "Save failed"));
    }
  };

  const sendToEditor = () => {
    if (!deck) return;
    sendSermonToEditor({
      title: deck.title || title || scripture,
      sermon_type: "slides", content: deck, navigate, t,
    });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-base">{t("scripture_to_slides") || "Scripture to Slides"}</CardTitle>
          <CardDescription>{t("scripture_slides_desc") || "Paste a passage; AI builds a ready presentation deck"}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">{t("scripture_reference") || "Scripture Reference"}</Label>
            <Input data-testid="slides-scripture-input" value={scripture} onChange={(e) => setScripture(e.target.value)}
              placeholder="e.g., Psalm 23 or John 3:16-18" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-400">{t("translation") || "Translation"}</Label>
              <Select value={translation} onValueChange={setTranslation}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRANSLATIONS.map((tx) => <SelectItem key={tx.value} value={tx.value}>{tx.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">{t("custom_title") || "Custom Title"}</Label>
              <Input data-testid="slides-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder={t("optional") || "Optional"} />
            </div>
          </div>
          <Button onClick={generate} disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="generate-slides-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}
            {loading ? (t("generating") || "Generating...") : (t("generate_slides") || "Generate Slides")}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">{deck?.title || (t("preview") || "Preview")}</CardTitle>
            <CardDescription>{deck ? `${deck.slides?.length || 0} ${t("slides") || "slides"}` : (t("no_deck_yet") || "No deck generated yet")}</CardDescription>
          </div>
          {deck && (
            <div className="flex gap-1">
              <ExportMenu title={deck.title || scripture} sermonType="slides" content={deck} t={t} testId="export-deck" />
              <Button variant="outline" size="sm" onClick={sendToEditor} data-testid="send-deck-to-editor-btn" className="text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10">
                <FileBox className="w-3.5 h-3.5 mr-1" />
                {t("send_to_editor") || "Send to Editor"}
              </Button>
              <Button variant="outline" size="sm" onClick={saveDeck} data-testid="save-deck-btn">
                <Save className="w-3.5 h-3.5 mr-1" />
                {t("save") || "Save"}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-2">
            {loading ? (
              <SkeletonDeck />
            ) : !deck ? (
              <EmptyState
                icon={BookOpen}
                title={t("deck_will_appear_here") || "Your slide deck will appear here"}
                description={t("scripture_empty_desc") || "Pick a passage on the left, or try one of these:"}
                examples={EXAMPLE_SCRIPTURES}
                onPick={(ex) => setScripture(ex.scripture)}
                testId="deck-empty"
              />
            ) : (
              <div className="space-y-3" data-testid="deck-result">
                {(deck.slides || []).map((sl, idx) => (
                  <div key={idx} className="p-3 rounded border border-slate-800 bg-slate-950/60">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-[10px] uppercase border-amber-500/40 text-amber-300">{sl.type}</Badge>
                      <span className="text-[10px] text-slate-500">#{idx + 1}</span>
                    </div>
                    <h4 className="font-semibold text-sm text-amber-200">{sl.heading}</h4>
                    {sl.scripture_ref && <p className="text-[10px] text-amber-400 mt-0.5">{sl.scripture_ref}</p>}
                    <p className="text-xs text-slate-200 mt-2 whitespace-pre-wrap">{sl.body}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
